import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signOut as fbSignOut,
  type User,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db, googleProvider } from "./firebase";

interface AuthState {
  user: User | null;
  /** `null` enquanto carrega, depois `true`/`false` conforme a allowlist. */
  allowed: boolean | null;
  loading: boolean;
  role: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Só no modo emulador: login sem popup, com credencial Google falsa. */
  signInDev?: (email: string) => Promise<void>;
}

const EMULATORS = import.meta.env.VITE_USE_EMULATORS === "1";

const Ctx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Conclui um fluxo de redirect (fallback quando o popup é bloqueado).
    getRedirectResult(auth).catch(() => {});
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setAllowed(null);
      setRole(null);
      if (u?.email) {
        try {
          const snap = await getDoc(doc(db, "support_users", u.email));
          setAllowed(snap.exists());
          setRole((snap.data()?.role as string) ?? null);
        } catch {
          setAllowed(false);
        }
      } else {
        setAllowed(false);
      }
      setLoading(false);
    });
  }, []);

  const value: AuthState = {
    user,
    allowed,
    role,
    loading,
    signIn: async () => {
      try {
        await signInWithPopup(auth, googleProvider);
      } catch (err) {
        const code = (err as { code?: string }).code;
        if (code === "auth/popup-blocked" || code === "auth/operation-not-supported-in-environment") {
          await signInWithRedirect(auth, googleProvider);
          return;
        }
        if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") return;
        throw err;
      }
    },
    signOut: async () => {
      await fbSignOut(auth);
    },
    signInDev: EMULATORS
      ? async (email: string) => {
          const idToken = JSON.stringify({ sub: email, email, email_verified: true });
          await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
        }
      : undefined,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth fora do AuthProvider");
  return v;
}
